import { useEffect, useState, useRef } from "react";
import { t } from "@twire/utility/helpers";
import { useLanguageChange } from "@twire/utility/hooks";
import { useSelector, useDispatch } from "react-redux";
import {
  getOngoing,
  getPast,
  getUpcoming,
  getPaginated,
  filterTournaments
} from "@twire/redux/tournaments/actions";
import {
  getPaginated as getScrimsPaginated,
  getScrims,
  filterScrims
} from "@twire/redux/scrims/actions";
import {
  PptType,
  TimeType,
  TournamentsInterface
} from "@twire/redux/tournaments/types";
import { RootReducer } from "@twire/redux/types";
import Tournaments from "@twire/common/Tournaments";
import { ComponentErrorInterface } from "@twire/common/ComponentError";
import { ReactComponent as TrophySvg } from "../../../../assets/svgs/trophy.svg";
import {
  filterPpt,
  getPptCup,
  getPptMaster,
  getPptScrim,
  getPaginated as getPptsPaginated
} from "@twire/redux/ppt/actions";
import { TournamentsViewProps } from "../Tournaments/TournamentsView";
import { PptInterface } from "@twire/redux/ppt/types";

const timeTypeActionMap: { [key: string]: () => void } = {
  ongoing: getOngoing,
  past: getPast,
  upcoming: getUpcoming
};

const pptTypeMap: { [key: string]: () => void } = {
  ppt_scrim: getPptScrim,
  ppt_cup: getPptCup,
  ppt_master: getPptMaster
};

const OngoingTournamentsContainer: React.FC<{
  type: TimeType;
  infinite?: boolean;
  mode?: TournamentsViewProps["mode"];
  pptType?: PptType;
}> = ({ type, infinite, mode = "tournament", pptType = 'ppt_scrim' }) => {
  const isScrim = mode === "scrim";
  const isPpt = mode === "ppt";
  const isTournament = mode === "tournament";
  const isUnmounting = useRef(false);
  const [error, setError] = useState<ComponentErrorInterface | null>(null);
  const dispatch = useDispatch();
  const lang = useLanguageChange();
  const {
    tournaments: { tournaments, requestStates },
    scrims: {
      scrims: {
        data,
        filter: scrimsFilters,
        page,
        isLastPage: isScrimsLastPage,
        game: scrimsGame
      },
      requestStates: scrimsRequestStates
    },
    ppt: { ppt, requestStates: pptRequestStates },
    app: { game }
  } = useSelector((state: RootReducer) => state);

  useEffect(() => {
    // dispatch(timeTypeActionMap[type]());
    if (isTournament) {
      if (
        tournaments[type as keyof TournamentsInterface["requestStates"]].game &&
        tournaments[type as keyof TournamentsInterface["requestStates"]]
          .game !== game
      ) {
        dispatch(timeTypeActionMap[type]());
      } else if (
        tournaments[type as keyof TournamentsInterface["requestStates"]].data
          .length === 0
      ) {
        dispatch(
          getPaginated(
            type,
            tournaments[type as keyof TournamentsInterface["requestStates"]]
              .page
          )
        );
      }
    }

    if (isScrim) {
      if (scrimsGame && scrimsGame !== game) {
        dispatch(getScrims());
      } else if (data.length === 0) {
        dispatch(getScrimsPaginated(page));
      }
    }

    if (isPpt) {
      if (
        ppt[pptType as keyof PptInterface["requestStates"]].game &&
        ppt[pptType as keyof PptInterface["requestStates"]].game !== game &&
        pptType
      ) {
        dispatch(pptTypeMap[pptType]());
      } else if (
        ppt[pptType as keyof PptInterface["requestStates"]].data.length === 0
      ) {
        dispatch(
          getPptsPaginated(
            ppt[pptType as keyof PptInterface["requestStates"]].page,
            pptType,
          )
        );
      }
    }
  }, [game, type, pptType]);

  useEffect(() => {
    return () => {
      isUnmounting.current = true;
    };
  }, []);

  useEffect(() => {
    return () => {
      if (isScrim) {
        if (
          isUnmounting.current &&
          (scrimsFilters.region || scrimsFilters.tier)
        ) {
          dispatch(
            filterScrims({
              region: "",
              tier: ""
            })
          );
        }
      }

      if (isPpt) {
        if (
          isUnmounting.current &&
          (ppt[pptType as keyof PptInterface["requestStates"]].filter.region ||
            ppt[pptType as keyof PptInterface["requestStates"]].filter.tier)
        ) {
          dispatch(
            filterPpt(
              {
                region: "",
                tier: ""
              },
              pptType
            )
          );
        }
      }

      if (isTournament) {
        if (
          isUnmounting.current &&
          (tournaments[type as keyof TournamentsInterface["requestStates"]]
            .filter.region ||
            tournaments[type as keyof TournamentsInterface["requestStates"]]
              .filter.tier)
        ) {
          dispatch(
            filterTournaments(
              {
                region: "",
                tier: ""
              },
              type
            )
          );
        }
      }
    };
  }, [
    scrimsFilters,
    ppt[pptType as keyof PptInterface["requestStates"]].filter,
    tournaments[type as keyof TournamentsInterface["requestStates"]].filter
  ]);

  const isLoading = () => {
    if (isScrim) {
      return (
        (data.length === 0 && scrimsRequestStates.data.requesting) ||
        scrimsRequestStates.filter.requesting
      );
    }
    if (isTournament) {
      return (
        (tournaments[type as keyof TournamentsInterface["requestStates"]].data
          .length === 0 &&
          requestStates[type as keyof TournamentsInterface["requestStates"]]
            .data.requesting) ||
        requestStates[type as keyof TournamentsInterface["requestStates"]]
          .filter.requesting
      );
    }
    if (isPpt && pptType) {
      return (
        (ppt[pptType as keyof PptInterface["requestStates"]].data.length ===
          0 &&
          pptRequestStates[pptType as keyof PptInterface["requestStates"]].data
            .requesting) ||
        pptRequestStates[pptType as keyof PptInterface["requestStates"]].filter
          .requesting
      );
    }
    return false;
  };

  useEffect(() => {
    let currentError = null;

    if (isTournament) {
      if (
        requestStates[type as keyof TournamentsInterface["requestStates"]].data
          .failed &&
        tournaments[type as keyof TournamentsInterface["requestStates"]].data
          .length === 0
      ) {
        currentError = {
          message: t("sentences.getTournamentsError"),
          suggestion: t("sentences.refreshThePage"),
          svg: TrophySvg,
          actionText: t("words.tryAgain"),
          onActionClicked: () =>
            dispatch(
              getPaginated(
                type,
                tournaments[type as keyof TournamentsInterface["requestStates"]]
                  .page
              )
            )
        };
      } else if (
        tournaments[type as keyof TournamentsInterface["requestStates"]].data
          .length === 0 &&
        tournaments[type as keyof TournamentsInterface["requestStates"]].filter
          .region === "" &&
        tournaments[type as keyof TournamentsInterface["requestStates"]].filter
          .tier === "" &&
        (requestStates[type as keyof TournamentsInterface["requestStates"]].data
          .success ||
          requestStates[type as keyof TournamentsInterface["requestStates"]]
            .filter.success)
      ) {
        currentError = {
          message: t("sentences.noTournamentsError"),
          suggestion: t("sentences.noTournamentsSuggestion"),
          svg: TrophySvg
        };
      } else if (
        requestStates[type as keyof TournamentsInterface["requestStates"]]
          .filter.success &&
        tournaments[type as keyof TournamentsInterface["requestStates"]].data
          .length === 0
      ) {
        currentError = {
          message: t("sentences.noFilterResultError"),
          suggestion: t("sentences.noFilterResultErrorSuggestion"),
          svg: TrophySvg
        };
      }
    }
    if (isScrim) {
      if (scrimsRequestStates.data.failed && data.length === 0) {
        currentError = {
          message: t("sentences.getTournamentsError"),
          suggestion: t("sentences.refreshThePage"),
          svg: TrophySvg,
          actionText: t("words.tryAgain"),
          onActionClicked: () =>
            dispatch(
              getPaginated(
                type,
                tournaments[type as keyof TournamentsInterface["requestStates"]]
                  .page
              )
            )
        };
      } else if (
        data.length === 0 &&
        scrimsFilters.region === "" &&
        scrimsFilters.tier === "" &&
        (scrimsRequestStates.data.success || scrimsRequestStates.filter.success)
      ) {
        currentError = {
          message: t("sentences.noTournamentsError"),
          suggestion: t("sentences.noTournamentsSuggestion"),
          svg: TrophySvg
        };
      } else if (scrimsRequestStates.filter.success && data.length === 0) {
        currentError = {
          message: t("sentences.noFilterResultError"),
          suggestion: t("sentences.noFilterResultErrorSuggestion"),
          svg: TrophySvg
        };
      }
    }
    if (isPpt) {
      if (
        pptRequestStates[pptType as keyof PptInterface["requestStates"]].data
          .failed &&
        ppt[pptType as keyof PptInterface["requestStates"]].data.length === 0
      ) {
        currentError = {
          message: t("sentences.getTournamentsError"),
          suggestion: t("sentences.refreshThePage"),
          svg: TrophySvg,
          actionText: t("words.tryAgain"),
          onActionClicked: () =>
            dispatch(
              getPaginated(
                ppt[pptType as keyof PptInterface["requestStates"]].page,
                pptType,
                
              )
            )
        };
      } else if (
        ppt[pptType as keyof PptInterface["requestStates"]].data.length === 0 &&
        ppt[pptType as keyof PptInterface["requestStates"]].filter.region ===
          "" &&
        ppt[pptType as keyof PptInterface["requestStates"]].filter.tier ===
          "" &&
        (pptRequestStates[pptType as keyof PptInterface["requestStates"]].data
          .success ||
          pptRequestStates[pptType as keyof PptInterface["requestStates"]]
            .filter.success)
      ) {
        currentError = {
          message: t("sentences.noTournamentsError"),
          suggestion: t("sentences.noTournamentsSuggestion"),
          svg: TrophySvg
        };
      } else if (
        pptRequestStates[pptType as keyof PptInterface["requestStates"]].filter
          .success &&
        ppt[pptType as keyof PptInterface["requestStates"]].data.length === 0
      ) {
        currentError = {
          message: t("sentences.noFilterResultError"),
          suggestion: t("sentences.noFilterResultErrorSuggestion"),
          svg: TrophySvg
        };
      }
    }

    setError(currentError);
  }, [
    requestStates,
    tournaments,
    scrimsRequestStates,
    data,
    ppt,
    pptRequestStates,
    pptType,
    lang,
    type,
    pptType
  ]);

  const onScroll = () => {
    if (isTournament) {
      dispatch(
        getPaginated(
          type,
          tournaments[type as keyof TournamentsInterface["requestStates"]]
            .page + 1
        )
      );
    } else if (isScrim) {
      dispatch(getScrimsPaginated(page + 1));
    } else if (isPpt) {
      dispatch(
        getPptsPaginated(
          ppt[pptType as keyof PptInterface["requestStates"]].page + 1,
          pptType,   
        )
      );
    }
  };

  const tournamentsData = {
    scrim: data,
    ppt: ppt[pptType as keyof PptInterface["requestStates"]].data,
    tournament:
      tournaments[type as keyof TournamentsInterface["requestStates"]].data
  };

  const infinitePag = {
    scrim: !isScrimsLastPage,
    ppt: !ppt[pptType as keyof PptInterface["requestStates"]].isLastPage,
    tournament:
      !tournaments[type as keyof TournamentsInterface["requestStates"]]
        .isLastPage
  };

  return (
    <Tournaments
      config={{}}
      loading={isLoading()}
      tournaments={tournamentsData[mode]}
      error={error}
      onScroll={onScroll}
      infinite={!!infinite && infinitePag[mode]}
    />
  );
};

export default OngoingTournamentsContainer;
